import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { ChecklistRequirement, Task } from "@recobra/schemas/task";
import { Checkbox } from "@recobra/ui/checkbox";

import { acceptedDocumentTypesLabel } from "../../documents";
import { ChecklistItemAction, type ChecklistItemAnswer } from "./ChecklistItemAction";
import { ChecklistRow, type ChecklistRowLink } from "./ChecklistRow";
import type { ChecklistRowDestination } from "./checklistRowLink";

/** One checklist row: a configured item is a checkbox; a requested document may also be concluded as nonexistent. */
export const RequirementChecklistItem: FC<{
  requirement: ChecklistRequirement;
  satisfied: boolean;
  task: Pick<Task, "id">;
  canWaive: boolean;
  onSatisfy: (requirementKey: string, satisfied: boolean) => void;
  onWaive: (requirementKey: string, waived: boolean) => void;
  waived: boolean;
  disabled: boolean;
  /** Where the assignee does the item, shown while it is pending. */
  link?: ChecklistRowDestination | null;
  /** The row's link navigates away from the modal's own route — close it so it doesn't linger over the destination. */
  onNavigate?: () => void;
}> = ({
  requirement,
  satisfied,
  waived,
  task,
  canWaive,
  onSatisfy,
  onWaive,
  disabled,
  link,
  onNavigate,
}) => {
  const answer = checklistItemAnswer(satisfied, waived);
  const pendingLink = answer === "pending" && link ? { ...link, disabled } : null;

  if (requirement.kind === "checklist_item") {
    return (
      <ConfiguredChecklistItem
        item={requirement}
        checkboxId={`${task.id}-${requirement.key}`}
        satisfied={satisfied}
        disabled={disabled}
        onSatisfy={onSatisfy}
        link={pendingLink}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <ChecklistRow
      title={acceptedDocumentTypesLabel(requirement.acceptedDocumentTypes)}
      detail={requestedDocumentDetail(requirement, answer)}
      satisfied={satisfied}
      action={
        disabled || (answer === "dismissed" && !canWaive) ? null : (
          <ChecklistItemAction
            answer={answer}
            onComplete={() => onSatisfy(requirement.key, true)}
            onDismiss={canWaive ? () => onWaive(requirement.key, true) : undefined}
            onRevert={() => (answer === "dismissed" ? onWaive : onSatisfy)(requirement.key, false)}
          />
        )
      }
      link={pendingLink}
      onNavigate={onNavigate}
    />
  );
};

/** A configured item: a checkbox, marked optional when nothing requires it. */
const ConfiguredChecklistItem: FC<{
  item: Extract<ChecklistRequirement, { kind: "checklist_item" }>;
  checkboxId: string;
  satisfied: boolean;
  disabled: boolean;
  onSatisfy: (requirementKey: string, satisfied: boolean) => void;
  link: ChecklistRowLink | null;
  onNavigate?: () => void;
}> = ({ item, checkboxId, satisfied, disabled, onSatisfy, link, onNavigate }) => (
  <ChecklistRow
    title={
      <label id={`${checkboxId}-label`} htmlFor={checkboxId}>
        {item.label}
      </label>
    }
    detail={item.required || item.groupId ? undefined : m.task_requirement_optional()}
    satisfied={satisfied}
    mark={
      <Checkbox
        id={checkboxId}
        aria-labelledby={`${checkboxId}-label`}
        checked={satisfied}
        disabled={disabled}
        onCheckedChange={(checked) => onSatisfy(item.key, checked)}
      />
    }
    link={link}
    onNavigate={onNavigate}
  />
);

const checklistItemAnswer = (satisfied: boolean, waived: boolean): ChecklistItemAnswer => {
  if (waived) return "dismissed";
  return satisfied ? "completed" : "pending";
};

const requestedDocumentDetail = (
  requirement: Extract<ChecklistRequirement, { kind: "requested_document" }>,
  answer: ChecklistItemAnswer,
): string | undefined => {
  const notes = [
    requirement.label,
    !requirement.required && m.case_requirements_required_later(),
    answer === "dismissed" && m.task_requirement_dismiss(),
  ].filter(Boolean);
  return notes.length > 0 ? notes.join(" · ") : undefined;
};
